import { fileUrl } from "@/lib/api";
import { parseMessage } from "@/lib/mentions";
import AttachmentCard, { isImage } from "@/components/chat/AttachmentCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Check, CheckCheck, X, Building2, Package } from "lucide-react";

const time = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export function MentionChip({ seg, mine, onClick }) {
  const isC = seg.kind === "contact";
  return (
    <button type="button" onClick={() => onClick && onClick(seg)} title={isC ? "Cari kartını aç" : "Stokta göster"}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-semibold align-baseline mx-0.5 transition-colors ${mine ? "bg-white/20 hover:bg-white/30 text-white" : isC ? "bg-secondary/10 text-secondary hover:bg-secondary/20" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
      data-testid={`chat-mention-chip-${seg.kind}-${seg.id}`}>
      {isC ? <Building2 className="h-3 w-3" /> : <Package className="h-3 w-3" />}{seg.label}
    </button>
  );
}

export function RichText({ text, mine, onMentionClick }) {
  return (
    <span className="text-sm whitespace-pre-wrap break-words">
      {parseMessage(text).map((s, i) => (s.type === "text" ? <span key={i}>{s.value}</span> : <MentionChip key={i} seg={s} mine={mine} onClick={onMentionClick} />))}
    </span>
  );
}

const IconBtn = ({ title, onClick, testid, children }) => (
  <button className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center" title={title} onClick={onClick} data-testid={testid}>{children}</button>
);

// Tek mesaj balonu: etiket çipleri, ekler (görsel/dosya), okundu bilgisi, düzenle/sil
export default function MessageBubble({ m, mine, isAdmin, editing, setEditing, startEdit, saveEdit, delMsg, onMentionClick }) {
  if (m.system) {
    return (
      <div className="flex justify-center" data-testid={`chat-system-${m.id}`}>
        <div className="max-w-[85%] rounded-xl border border-secondary/30 bg-secondary/5 px-3 py-2 text-xs text-center">
          <div className="font-semibold text-secondary">{m.deleted ? <span className="italic text-muted-foreground font-normal">Bildirim silindi</span> : m.text}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{m.sender_name} · {time(m.created_at)}{isAdmin && !m.deleted && <button className="ml-2 underline" onClick={() => delMsg(m)} data-testid={`chat-del-${m.id}`}>sil</button>}</div>
        </div>
      </div>
    );
  }
  const isEditing = editing?.id === m.id;
  const atts = m.attachments || [];
  const imgs = atts.filter(isImage);
  const files = atts.filter((a) => !isImage(a));
  return (
    <div className={`group flex items-end gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      {mine && !m.deleted && !isEditing && (
        <div className="flex gap-0.5 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity mb-1">
          {m.text && <IconBtn title="Düzenle" onClick={() => startEdit(m)} testid={`chat-edit-${m.id}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></IconBtn>}
          <IconBtn title="Sil" onClick={() => delMsg(m)} testid={`chat-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></IconBtn>
        </div>
      )}
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${m.deleted ? "bg-muted/60 border border-dashed" : mine ? "bg-primary text-white rounded-br-sm" : "bg-card border rounded-bl-sm"}`}>
        {!mine && <div className="text-[11px] font-bold opacity-70 mb-0.5">{m.sender_name}</div>}
        {m.deleted ? (
          <div className="text-xs italic text-muted-foreground" data-testid={`chat-deleted-${m.id}`}>Bu mesaj silindi</div>
        ) : isEditing ? (
          <div className="flex items-center gap-1 min-w-[240px]">
            <Input value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }} autoFocus className="h-8 text-sm text-foreground" data-testid="chat-edit-input" />
            <Button size="icon" variant="secondary" className="h-8 w-8 shrink-0" onClick={saveEdit} data-testid="chat-edit-save"><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-white hover:text-foreground" onClick={() => setEditing(null)} data-testid="chat-edit-cancel"><X className="h-4 w-4" /></Button>
          </div>
        ) : (
          <>
            {m.image_url && <a href={fileUrl(m.image_url)} target="_blank" rel="noreferrer"><img src={fileUrl(m.image_url)} alt="ek" className="rounded-lg max-h-64 mb-1 object-contain" /></a>}
            {imgs.length > 0 && (
              <div className={`grid gap-1.5 mb-1 ${imgs.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid={`chat-images-${m.id}`}>
                {imgs.map((a, i) => <AttachmentCard key={a.url || i} a={a} mine={mine} testid={`chat-att-${m.id}-${i}`} />)}
              </div>
            )}
            {files.length > 0 && (
              <div className="space-y-1.5 mb-1" data-testid={`chat-files-${m.id}`}>
                {files.map((a, i) => <AttachmentCard key={a.url || i} a={a} mine={mine} testid={`chat-att-${m.id}-${imgs.length + i}`} />)}
              </div>
            )}
            {m.text && <RichText text={m.text} mine={mine} onMentionClick={onMentionClick} />}
          </>
        )}
        <div className={`text-[10px] mt-0.5 flex items-center justify-end gap-1 ${mine && !m.deleted ? "text-white/70" : "text-muted-foreground"}`}>
          <span>{m.edited_at && !m.deleted ? "düzenlendi · " : ""}{time(m.created_at)}</span>
          {mine && !m.deleted && (m.read
            ? <span className="inline-flex items-center gap-0.5" title={`Okundu ${time(m.read_at)}${m.read_by ? " · " + m.read_by : ""}`} data-testid={`chat-read-${m.id}`}><CheckCheck className="h-3.5 w-3.5 text-sky-300" /><span className="hidden sm:inline">Okundu {time(m.read_at)}</span></span>
            : <span title="İletildi · henüz okunmadı" data-testid={`chat-sent-${m.id}`}><Check className="h-3.5 w-3.5 opacity-70" /></span>)}
        </div>
      </div>
      {!mine && isAdmin && !m.deleted && (
        <button className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity mb-1" title="Sil (yönetici)" onClick={() => delMsg(m)} data-testid={`chat-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
      )}
    </div>
  );
}
